import { css } from '@emotion/react';
import { FC, ReactNode, useState } from 'react';
import { CollapsibleSection } from '../../../components/CollapsibleSection/CollapsibleSection';
import { mutedCaptionCss } from '../../CompanyProfiles/components/ActivityDrawer/activity-drawer-common.styles';
import { sectionCardCss, sectionTitleCss } from './deals-detail.styles';

// Air between the card title and its inline annotation/subtitle, same on every card.
const summaryGapCss = css`
  display: inline-flex;
  min-width: 0;
  margin-left: 8px;
`;

interface CollapsibleSectionCardProps {
  title: string;
  /** aria-label for the section; defaults to title (Transaction's label omits the "(My funds only)" suffix). */
  ariaLabel?: string;
  /** Drives the collapsed default and the header annotation (with defaultCollapsed) until the user toggles. */
  isEmpty: boolean;
  /** Muted note next to the title while empty. */
  emptyAnnotation?: string;
  /** Always-visible header content, right-aligned (Documents badge). */
  headerRight?: ReactNode;
  /** Muted line inline after the title while the card has data. */
  subtitle?: ReactNode;
  /** Starts collapsed even with data (SAFE / note rounds); the user can still expand it. */
  defaultCollapsed?: boolean;
  children: ReactNode;
}

/**
 * Deals-tab card chrome around the shared CollapsibleSection (left chevron
 * rail). Blank cards start collapsed and expansion follows the data until the
 * user toggles; a manual toggle wins until remount (the pane remounts per round).
 */
export const CollapsibleSectionCard: FC<CollapsibleSectionCardProps> = ({
  title,
  ariaLabel,
  isEmpty,
  emptyAnnotation = 'No data',
  headerRight,
  subtitle,
  defaultCollapsed = false,
  children,
}) => {
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
  const expanded = userExpanded ?? (!isEmpty && !defaultCollapsed);

  return (
    <section css={sectionCardCss} aria-label={ariaLabel ?? title}>
      <CollapsibleSection
        title={title}
        titleCss={sectionTitleCss}
        expanded={expanded}
        onExpandedChange={setUserExpanded}
        summary={
          <span css={summaryGapCss}>
            {isEmpty ? <span css={mutedCaptionCss}>{emptyAnnotation}</span> : subtitle}
          </span>
        }
        actions={headerRight}
        keepMounted
      >
        {children}
      </CollapsibleSection>
    </section>
  );
};
